import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Check, X, Plus, Trash, Download } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для планирования международных встреч с учетом часовых поясов
 */
const MeetingPlanner = ({ 
  formData, 
  setFormData,
  russianCities,
  worldCities
}) => {
  const [participants, setParticipants] = useState([
    { id: '1', city: formData.sourceCity, timezone: formData.sourceTimezone, name: 'Участник 1', workHours: { start: '09:00', end: '18:00' } },
    { id: '2', city: formData.targetCity, timezone: formData.targetTimezone, name: 'Участник 2', workHours: { start: '09:00', end: '18:00' } }
  ]);
  
  const [meetingDate, setMeetingDate] = useState(formData.date);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [meetingTitle, setMeetingTitle] = useState('Международная встреча');
  const [meetingDuration, setMeetingDuration] = useState('60');
  const [showResults, setShowResults] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // При изменении основных городов обновляем соответствующих участников
  useEffect(() => {
    setParticipants(prev => {
      const newParticipants = [...prev];
      
      // Обновляем первого участника, если он есть
      if (newParticipants.length > 0) {
        const sourceCity = [...russianCities, ...worldCities].find(c => c.id === formData.sourceCity);
        if (sourceCity) {
          newParticipants[0] = {
            ...newParticipants[0],
            city: formData.sourceCity,
            timezone: sourceCity.timezone
          };
        }
      }
      
      // Обновляем второго участника, если он есть
      if (newParticipants.length > 1) {
        const targetCity = [...russianCities, ...worldCities].find(c => c.id === formData.targetCity);
        if (targetCity) {
          newParticipants[1] = {
            ...newParticipants[1],
            city: formData.targetCity,
            timezone: targetCity.timezone
          };
        }
      }
      
      return newParticipants;
    });
    
    setMeetingDate(formData.date);
  }, [formData.sourceCity, formData.targetCity, formData.date, russianCities, worldCities]);

  // Добавить участника
  const addParticipant = () => {
    const newId = String(participants.length + 1);
    setParticipants([
      ...participants,
      {
        id: newId,
        city: 'Moscow',
        timezone: 'Europe/Moscow',
        name: `Участник ${newId}`,
        workHours: { start: '09:00', end: '18:00' }
      }
    ]);
  };

  // Удалить участника
  const removeParticipant = (id) => {
    if (participants.length <= 2) {
      alert('Необходимо минимум два участника');
      return;
    }
    setParticipants(participants.filter(p => p.id !== id));
  };

  // Обновить данные участника
  const updateParticipant = (id, field, value) => {
    setParticipants(participants.map(p => {
      if (p.id === id) {
        if (field === 'city') {
          // Обновляем и timezone при изменении города
          const city = [...russianCities, ...worldCities].find(c => c.id === value);
          return {
            ...p,
            [field]: value,
            timezone: city ? city.timezone : p.timezone
          };
        } else if (field.startsWith('workHours.')) {
          // Обновление рабочих часов
          const subField = field.split('.')[1];
          return {
            ...p,
            workHours: {
              ...p.workHours,
              [subField]: value
            }
          };
        } else {
          return { ...p, [field]: value };
        }
      }
      return p;
    }));
  };

  // Рассчитать доступные слоты для встречи
  const calculateAvailableSlots = () => {
    setIsCalculating(true);
    setShowResults(true);
    setAvailableSlots([]);
    setSelectedSlot(null);
    
    // Откладываем расчеты для обновления UI
    setTimeout(() => {
      try {
        const allCities = [...russianCities, ...worldCities];
        const durationMinutes = parseInt(meetingDuration, 10);
        
        // Получаем дату встречи
        const meetingDateObj = new Date(`${meetingDate}T00:00:00`);
        
        // Генерируем все возможные слоты с шагом 30 минут
        const slots = [];
        const slotCount = 48; // 24 часа по 30 минут
        
        for (let i = 0; i < slotCount; i++) {
          const slotTime = new Date(meetingDateObj);
          slotTime.setHours(Math.floor(i / 2), (i % 2) * 30, 0);
          
          // Для каждого слота проверяем доступность всех участников
          const participantTimes = participants.map(participant => {
            const city = allCities.find(c => c.id === participant.city);
            const timezone = city ? city.timezone : participant.timezone;
            
            // Форматируем время в часовом поясе участника
            const options = {
              timeZone: timezone,
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            };
            
            const localTime = new Intl.DateTimeFormat('ru-RU', options).format(slotTime);
            
            // Проверяем, находится ли время в рабочих часах участника
            const [hour, minute] = localTime.split(':').map(Number);
            const [startHour, startMinute] = participant.workHours.start.split(':').map(Number);
            const [endHour, endMinute] = participant.workHours.end.split(':').map(Number);
            
            const timeInMinutes = hour * 60 + minute;
            const startInMinutes = startHour * 60 + startMinute;
            const endInMinutes = endHour * 60 + endMinute;
            
            // Проверяем, что время встречи полностью укладывается в рабочее время
            const isAvailable = timeInMinutes >= startInMinutes && 
                              (timeInMinutes + durationMinutes) <= endInMinutes;
            
            return {
              time: localTime,
              isAvailable,
              timezone
            };
          });
          
          // Слот доступен, только если он доступен для всех участников
          const isSlotAvailable = participantTimes.every(pt => pt.isAvailable);
          
          if (isSlotAvailable) {
            // Форматируем время в московском часовом поясе (как основное)
            const moscowOptions = {
              timeZone: 'Europe/Moscow',
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            };
            
            const moscowTime = new Intl.DateTimeFormat('ru-RU', moscowOptions).format(slotTime);
            
            slots.push({
              time: slotTime.toISOString(),
              moscowTime,
              participantTimes
            });
          }
        }
        
        setAvailableSlots(slots);
        
        // Автоматически выбираем первый доступный слот, если он есть
        if (slots.length > 0) {
          setSelectedSlot(slots[0]);
        }
      } catch (error) {
        console.error("Ошибка расчета слотов:", error);
        alert("Произошла ошибка при расчете доступных слотов. Пожалуйста, проверьте введенные данные.");
      } finally {
        setIsCalculating(false);
      }
    }, 100);
  };

  // Экспорт встречи в формате ICS (iCalendar)
  const exportToICS = () => {
    if (!selectedSlot) return;
    
    const meetingTimeObj = new Date(selectedSlot.time);
    const endTimeObj = new Date(meetingTimeObj.getTime() + parseInt(meetingDuration, 10) * 60000);
    
    const formatDate = (date) => {
      return date.toISOString().replace(/-|:|\.\d+/g, '');
    };
    
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Gensio//Планировщик встреч//RU',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `SUMMARY:${meetingTitle}`,
      `DTSTART:${formatDate(meetingTimeObj)}`,
      `DTEND:${formatDate(endTimeObj)}`,
      `DESCRIPTION:Международная встреча с участниками из ${participants.map(p => {
        const city = [...russianCities, ...worldCities].find(c => c.id === p.city);
        return city ? city.name : p.city;
      }).join(', ')}`,
      `LOCATION:Онлайн`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');
    
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${meetingTitle.replace(/\s+/g, '_')}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
        <Calendar className="w-5 h-5 mr-2 text-purple-500" />
        Планировщик международных встреч
      </h2>

      {/* Форма настройки встречи */}
      <div className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="meetingTitle" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Название встречи
            </label>
            <input
              type="text"
              id="meetingTitle"
              value={meetingTitle}
              onChange={(e) => setMeetingTitle(e.target.value)}
              className="form-input w-full"
              placeholder="Введите название встречи"
            />
          </div>

          <div>
            <label htmlFor="meetingDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Дата встречи
            </label>
            <input
              type="date"
              id="meetingDate"
              value={meetingDate}
              onChange={(e) => setMeetingDate(e.target.value)}
              className="form-input w-full"
            />
          </div>

          <div>
            <label htmlFor="meetingDuration" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Продолжительность (минуты)
            </label>
            <select
              id="meetingDuration"
              value={meetingDuration}
              onChange={(e) => setMeetingDuration(e.target.value)}
              className="form-select w-full"
            >
              <option value="30">30 минут</option>
              <option value="60">1 час</option>
              <option value="90">1 час 30 минут</option>
              <option value="120">2 часа</option>
              <option value="180">3 часа</option>
            </select>
          </div>
        </div>
      </div>

      {/* Секция участников */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
            <Users className="w-5 h-5 mr-2 text-purple-500" />
            Участники встречи
          </h3>
          <Button
            size="sm"
            onClick={addParticipant}
            icon={<Plus className="w-4 h-4" />}
            variant="outline"
          >
            Добавить участника
          </Button>
        </div>

        <div className="space-y-4">
          {participants.map((participant) => (
            <div 
              key={participant.id} 
              className="p-4 bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div className="flex flex-wrap gap-4">
                <div className="flex-grow min-w-[200px]">
                  <label htmlFor={`participant-name-${participant.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Имя участника
                  </label>
                  <input
                    type="text"
                    id={`participant-name-${participant.id}`}
                    value={participant.name}
                    onChange={(e) => updateParticipant(participant.id, 'name', e.target.value)}
                    className="form-input w-full"
                  />
                </div>

                <div className="flex-grow min-w-[200px]">
                  <label htmlFor={`participant-city-${participant.id}`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Город
                  </label>
                  <select
                    id={`participant-city-${participant.id}`}
                    value={participant.city}
                    onChange={(e) => updateParticipant(participant.id, 'city', e.target.value)}
                    className="form-select w-full"
                  >
                    <optgroup label="Россия">
                      {russianCities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name} (UTC{city.utcOffset})
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Мир">
                      {worldCities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name} (UTC{city.utcOffset})
                        </option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                <div className="flex-grow min-w-[240px]">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Рабочие часы
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={participant.workHours.start}
                      onChange={(e) => updateParticipant(participant.id, 'workHours.start', e.target.value)}
                      className="form-input w-full"
                    />
                    <span className="text-gray-500 dark:text-gray-400 self-center">—</span>
                    <input
                      type="time"
                      value={participant.workHours.end}
                      onChange={(e) => updateParticipant(participant.id, 'workHours.end', e.target.value)}
                      className="form-input w-full"
                    />
                  </div>
                </div>

                <div className="flex items-end justify-end pb-1">
                  {participants.length > 2 && (
                    <button 
                      onClick={() => removeParticipant(participant.id)}
                      className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      aria-label="Удалить участника"
                    >
                      <Trash className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Кнопка расчета */}
      <div className="mt-6 mb-8">
        <Button
          primary
          size="lg"
          onClick={calculateAvailableSlots}
          icon={<Calendar className="w-5 h-5" />}
          isLoading={isCalculating}
          disabled={isCalculating}
        >
          {isCalculating ? 'Расчет времени...' : 'Найти подходящее время'}
        </Button>
      </div>

      {/* Результаты расчета */}
      {showResults && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-purple-500" />
            Доступное время для встречи
          </h3>

          {isCalculating ? (
            <div className="py-10 text-center text-gray-500 dark:text-gray-400">
              <div className="animate-spin inline-block w-8 h-8 border-4 border-purple-200 border-t-purple-600 dark:border-purple-700 dark:border-t-purple-400 rounded-full mb-4"></div>
              <p>Рассчитываем подходящее время для всех участников...</p>
            </div>
          ) : availableSlots.length === 0 ? (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-800 rounded-lg p-4 text-center">
              <X className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
              <h4 className="text-yellow-700 dark:text-yellow-300 font-medium">Нет подходящего времени</h4>
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                К сожалению, не удалось найти время, подходящее всем участникам. 
                Попробуйте изменить рабочие часы или выбрать другую дату.
              </p>
            </div>
          ) : (
            <>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg p-4 text-center mb-6">
                <Check className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <h4 className="text-green-700 dark:text-green-300 font-medium">Найдено {availableSlots.length} подходящих слотов</h4>
                <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                  Выберите удобное время для проведения встречи.
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 mb-6">
                {availableSlots.map((slot, index) => (
                  <button
                    key={index}
                    className={`p-3 border rounded-lg text-center transition-colors ${
                      selectedSlot === slot 
                        ? 'bg-purple-100 border-purple-300 text-purple-800 dark:bg-purple-900/50 dark:border-purple-700 dark:text-purple-200' 
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                    onClick={() => setSelectedSlot(slot)}
                  >
                    <div className="font-medium">{slot.moscowTime}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">МСК</div>
                  </button>
                ))}
              </div>

              {selectedSlot && (
                <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 mb-6">
                  <h4 className="text-base font-medium text-gray-900 dark:text-white mb-3">
                    Детали выбранного времени
                  </h4>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Встреча:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{meetingTitle}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Дата:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{meetingDate}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Время (МСК):</span>
                      <span className="font-medium text-gray-900 dark:text-white">{selectedSlot.moscowTime}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Продолжительность:</span>
                      <span className="font-medium text-gray-900 dark:text-white">{meetingDuration} мин.</span>
                    </div>
                  </div>

                  <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                    Локальное время участников:
                  </h5>
                  
                  <div className="space-y-2 mb-4">
                    {participants.map((participant, index) => {
                      const participantTime = selectedSlot.participantTimes[index];
                      return (
                        <div key={participant.id} className="flex items-center justify-between">
                          <span className="text-gray-600 dark:text-gray-400">{participant.name}:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {participantTime.time}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    primary
                    fullWidth
                    size="md"
                    onClick={exportToICS}
                    icon={<Download className="w-4 h-4" />}
                  >
                    Экспортировать встречу (ICS)
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MeetingPlanner;